import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Avatar } from '@saleshandy/design-system';
import { Pencil } from '@saleshandy/icons';
import {
  getSequenceStatus,
  getSequenceStepType,
} from '../../../../../utils/helper';
import { getFormattedTime } from '../../../../../../../../../shared/utils';

import {
  getOptionFromOutcome,
  mapOutcomeToOption,
} from '../../../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/prospect-outcome-select/utils/helper';
import GenericCustomOutcomeDropdown from '../../../../../../../../settings/components/custom-outcomes/components/custom-outcome-dropdown';
import { getOutcomeIcon } from '../../../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/helpers/step-filter-mapper';
import { OverlayTooltip } from '../../../../../../../../../shared/design-system/components/overlay';
import { Placement } from '../../../../../../../../../shared/design-system/components/overlay-taget/enums';
import ContactStatusTag from '../../../../../../../../../shared/design-system/components/atoms/contact-status-tag/contact-status-tag';
import { ContactStatusTypeDisplayName } from '../../../../../../../../sequence/enums';
import ActionsDropdown from './actions-dropdown';
import { trimLeadingZeros } from '../../../../../../../../../shared/utils/trim-leading-zeros';
import { saveProspectDealValue } from '../../../../../../../../sequence/helpers/sequence.api';
import toaster from '../../../../../../../../../shared/toaster';

const ProspectSequenceList = ({
  row,
  onAction,
  selectedOutcome,
  allOutcomes,
  setSelectedOutcome,
  generateActionsColumn,
}) => {
  const initialDealValue = row?.sequences?.sequenceProspectDealValue
    ? `$${row.sequences.sequenceProspectDealValue}`
    : `$0`;
  const [dealValue, setDealValue] = useState(initialDealValue);
  const [isEdited, setIsEdited] = useState(false);
  const [isEditMode, setEditMode] = useState(false);

  const iconContainerRef = useRef(null);
  const inputRef = useRef(null);

  const iconClassNames = classNames('gray-txt-12', 'regular-4', {
    'icon-deal-remove': isEditMode,
    'deal-icon-wrap': !isEditMode,
  });
  const inputValueClassNames = classNames(
    'deal-value-input',
    'popover-arrow-color-txt',
    {
      'deal-value-icon-btn-cls-border': !isEditMode,
      'deal-value-icon-btn-cls': isEditMode,
    },
  );
  const onInputChange = (event) => {
    const { value } = event.target;
    setIsEdited(true);
    let updatedValue = value;
    if (updatedValue.charAt(0) !== '$') {
      updatedValue = `$ ${updatedValue.substr(1)}`;
    }

    const isValidInput = /^\$\s?\d*$/.test(updatedValue);
    if (isValidInput) {
      setDealValue(updatedValue);
    }
  };

  const deactivateEditMode = async () => {
    setEditMode(false);
  };

  const onRevert = () => {
    const previousTitle = '$1000';
    setDealValue(previousTitle);
    inputRef.current.blur();
    deactivateEditMode();
  };

  const onInputKeyPress = (event) => {
    if (event.key === 'Enter') {
      inputRef.current.blur();
    } else if (event.key === 'Escape') {
      onRevert();
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        iconContainerRef.current &&
        !iconContainerRef.current.contains(event.target)
      ) {
        deactivateEditMode();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const onSave = async (seq) => {
    if (dealValue.trim().length > 1) {
      const prospectId = seq?.sequences.prospectId;
      const sequenceId = seq?.sequences.id;
      deactivateEditMode();

      if (isEdited) {
        let trimmedValue = trimLeadingZeros(parseInt(dealValue.substr(1), 10));
        trimmedValue = `$${trimmedValue}`;
        setDealValue(trimmedValue);
        try {
          await saveProspectDealValue(
            sequenceId,
            parseInt(prospectId, 10),
            parseInt(trimmedValue.substr(1), 10),
          );
          toaster.success('Deal value updated successfully');
        } catch (err) {
          toaster.error('Deal value updation failed');
        }
        setIsEdited(false);
      }
    } else {
      setDealValue(initialDealValue);
    }
  };

  const onInputBlur = (sequence) => {
    onSave(sequence);
  };

  const activateEditMode = () => {
    setEditMode(true);
  };

  const handleDealIcon = () => {
    if (inputRef.current) {
      // Do something with the input box, for example, focus it
      inputRef.current.focus();
    }
    activateEditMode();
  };

  const isDealValueVisible = (outcomeId: number) =>
    [2, 4, 6]?.includes(outcomeId);

  const isSubsequence = row?.sequences?.type === 'subsequence';

  return (
    <div
      className="prospect-sequence-list-container"
      key={`${row?.id}-${row?.status?.updatedAt}`}
    >
      <div className="prospect-sequence-list-content">
        <a
          href={
            isSubsequence
              ? `/sequence/${row?.sequences?.parentSequence?.id}/subsequence/${row?.sequences?.id}/steps`
              : `/sequence/${row?.sequences?.parentSequence?.id}/steps`
          }
          target="_blank"
          rel="noreferrer"
          className="seqeuence-name"
        >
          {row?.sequences?.value}
        </a>
        <span className="sequence-status-step">
          <span className="sequence-step-progress">
            {getSequenceStatus(row?.sequences?.step?.sequence?.progress)}
          </span>
          <span className="divider-bar" />
          <div className="d-flex align-items-center">
            <span className="d-flex align-items-center">
              {getSequenceStepType(row?.sequences?.step?.type)}
            </span>
            <span className="sequence-step-number">
              {row?.sequences?.stepNumber}
            </span>
          </div>
          <span className="divider-bar" />
          <GenericCustomOutcomeDropdown
            showFooter={false}
            options={mapOutcomeToOption(allOutcomes)}
            showTotal={false}
            className="prospect-outcome-select"
            onChange={(option) => {
              setSelectedOutcome(option);
            }}
            changeOutcomeRequestPayload={{
              sequenceId: row.sequences.id,
              prospectId: row.sequences.prospectId,
            }}
            initialSelectedOption={getOptionFromOutcome(
              allOutcomes,
              row.sequences.outcomeId,
            )}
            optionRenderer={(option) => (
              <div className="d-flex align-items-center">
                {getOutcomeIcon(option?.name, false, {
                  isDefault: option.isDefault,
                  sentiment: option.sentiment,
                })}
                <span className="ml-1">{option.name}</span>
              </div>
            )}
          />
          {isDealValueVisible(row.sequences.outcomeId) &&
            row?.sequences?.sequenceProspectDealValue !== null && (
              <div className="deal-value-prospect d-flex align-items-center ml-1">
                <input
                  className={inputValueClassNames}
                  value={dealValue}
                  onChange={onInputChange}
                  onBlur={() => onInputBlur(row)}
                  onKeyUp={onInputKeyPress}
                  ref={inputRef}
                  maxLength={8}
                  size={dealValue?.length ?? 2}
                  readOnly={!isEditMode}
                  onDoubleClick={() => setEditMode(true)}
                />
                <OverlayTooltip
                  text="Modify Deal Value for this Prospect"
                  className="tooltip__max-width"
                  placement={Placement.Bottom}
                >
                  <Pencil
                    ref={iconContainerRef}
                    className={`${iconClassNames} `}
                    onClick={handleDealIcon}
                  />
                </OverlayTooltip>
              </div>
            )}
        </span>
      </div>
      <div className="prospect-sequence-list-actions">
        <div className="prospect-status-dropdown-actions">
          <ContactStatusTag
            status={row.status.value}
            isSequenceProspectTag={true}
            showTooltip
            tooltipText={`Latest Status: ${
              ContactStatusTypeDisplayName[row.status?.value]
            }`}
            placement={Placement.Bottom}
          />
          <ActionsDropdown
            actions={generateActionsColumn(row)}
            onActionClick={(actionKey) => onAction(actionKey, row)}
          />
        </div>
        <div className="d-flex align-items-center gap-4">
          <span className="blue-txt-15 white-space-nowrap prospect-last-activity">
            {`Last activity ${getFormattedTime(row.status.updatedAt)}`}
          </span>
          <OverlayTooltip
            text={`${row.sequences.userFirstName} ${row.sequences.userLastName}`}
          >
            <div className="d-flex align-items-center">
              <Avatar
                firstName={row?.sequences?.userFirstName}
                lastName={row?.sequences?.userLastName}
              />
            </div>
          </OverlayTooltip>
        </div>
      </div>
    </div>
  );
};

export default ProspectSequenceList;
